import {Grid, GridProps, Text} from "@mantine/core";
import {Address, AddressEntity, trimOrUndefined} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import { DefaultText } from "../../text/DefaultText/DefaultText";

export type FormatAddressOpts = {
  singleLine?: boolean;
  includeCountry?: boolean;
  includeCounty?: boolean;
  delimiter?: string;
  lineBreak?: string;
  uppercaseUSState?: boolean;
};

const pickRegion = (addr: AddressEntity | Address) => {
  const country = trimOrUndefined(addr.country)?.toUpperCase();
  const state = trimOrUndefined(addr.state);
  const province = trimOrUndefined(addr.province);
  if (country === 'US') return state ?? province;
  if (country === 'CA') return province ?? state;
  return province ?? state;
};

export function formatAddress(
  address: AddressEntity | Address,
  opts: FormatAddressOpts = {}
): string {
  const {
    singleLine = true,
    includeCountry = false,
    includeCounty = true,
    delimiter = ', ',
    lineBreak = '\n',
    uppercaseUSState = true,
  } = opts;

  const street = trimOrUndefined(address.street);
  const street2 = trimOrUndefined(address.street2);
  const city = trimOrUndefined(address.city);
  const county = includeCounty ? trimOrUndefined(address.county) : undefined;
  const postal = trimOrUndefined(address.postalCode);
  const country = trimOrUndefined(address.country);

  let region = trimOrUndefined(pickRegion(address));
  if (
    uppercaseUSState &&
    (country?.toUpperCase() === 'US' || (!country && (region?.length ?? 0) <= 3))
  ) {
    region = region?.toUpperCase();
  }

  const line1 = [street, street2].filter(Boolean).join(', ');

  let line2 = '';
  const cityCounty = [city, county].filter(Boolean).join(', ') || undefined;

  if (cityCounty && region) {
    line2 = postal ? `${cityCounty}, ${region} ${postal}` : `${cityCounty}, ${region}`;
  } else {
    line2 = [cityCounty, region, postal].filter(Boolean).join(', ');
  }

  const line3 = includeCountry ? country : undefined;

  const parts = [line1, line2, line3].filter(Boolean) as string[];

  return singleLine ? parts.join(delimiter) : parts.join(lineBreak);
}

export const formatAddressOneLine = (addr: AddressEntity | Address, includeCountry = false) =>
  formatAddress(addr, {singleLine: true, includeCountry});

export const formatAddressMultiLine = (addr: AddressEntity | Address, includeCountry = false) =>
  formatAddress(addr, {singleLine: false, includeCountry});

const pick = (d: AddressSummaryProps['address'], k: keyof (AddressEntity | Address)) => {
  const v = (d as any)[k] ?? d?.[k];
  const s = (v ?? '').toString().trim();
  return s.length ? s : undefined;
};

const AddressSummaryRow = ({label, value}: { label: string; value?: string }) =>
  <Grid.Col span={6}>
    <Text ta="left" fz="sm" weight={500} mt="md">
      {label}
    </Text>
    <Text ta="left" c="dimmed" fz="xs">
     <DefaultText value={value} defaultValue="(Not Defined)"/>
    </Text>
  </Grid.Col>

export type AddressSummaryProps = Omit<GridProps, 'children'> & {
  address?: AddressEntity | Address
};

export const AddressSummary = (props: AddressSummaryProps) => {
  const {address, ...rest} = props

  const {t} = useTranslation(["common"])

  const order: (keyof Address)[] = ['street', 'street2', 'city', 'county', 'state', 'province', 'postalCode', 'country'];

  const labels: Record<keyof (Address), string> = {
    street: t(`common.address.street.label`, 'Street'),
    street2: t(`common.address.street2.label`, 'Street 2'),
    city: t(`common.address.city.label`, 'City'),
    county: t(`common.address.county.label`, 'County'),
    state: t(`common.address.state.label`, 'State'),
    postalCode: t(`common.address.postalcode.label`, 'Postal Code'),
    country: t(`common.address.country.label`, 'Country'),
    province: t(`common.address.province.label`, 'Province'),
  };

  return (
    <Grid {...rest}>
      {order.map((k) => (
        <AddressSummaryRow key={k} label={labels[k]} value={pick(address, k)}/>
      ))}
    </Grid>
  )
};
